import { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import Modal from '../../../common/Modal';
import CopyLink from '../../../common/CopyLink';
import { LiveReportCrudContext } from '../context/LiveReportCrudContext';
import { getOpenUrl, getPrintUrl } from '../utils/editorUrl';
import spinner from '../../../../images/spinner-ripple.svg';

/**
 * Format a 6-digit code as `123-456` for readability. Storage stays unformatted.
 * @param {string|null|undefined} code
 */
const formatCode = (code) => {
  if (!code || code.length !== 6) return code || '';
  return `${code.slice(0, 3)}-${code.slice(3)}`;
};

/**
 * @param {object} props
 * @param {boolean} props.modalOpen
 * @param {(open: boolean) => void} props.setModalOpen
 * @param {import('../../../../types/LiveReports2').LiveReports2Doc} props.report - Report doc; `ShareCode` is read live from props (updates via parent's Firestore subscription).
 */
const ShareReportModal = ({ modalOpen, setModalOpen, report }) => {

  const history = useHistory();
  const crudContext = useContext(LiveReportCrudContext);
  if (!crudContext) {
    throw new Error('LiveReportCrudContext not found.');
  }
  const { generateShareCode, clearShareCode } = crudContext;

  /** @type {ReturnType<typeof useState<boolean>>} */
  const [busy, setBusy] = useState(false);

  /** @type {ReturnType<typeof useState<string|null>>} */
  const [error, setError] = useState(null);

  /** @type {ReturnType<typeof useState<string|null>>} */
  const [copied, setCopied] = useState(null);

  /** @type {ReturnType<typeof useState<'switch-to-open' | 'regenerate' | null>>} */
  const [pendingConfirm, setPendingConfirm] = useState(null);

  const code = report.ShareCode || null;
  const mode = code ? 'secure' : 'open';
  // A report about one participant carries personal data, so it is shareable
  // with a code only. This is the explanation; the real gate is server-side
  // (resolvePublicReport). AI-CONTEXT: See docs/features/livereport-per-deelnemer.md
  const codeRequired = Boolean(report.PersonaID);
  const url = `${window.location.origin}${getOpenUrl(report.ID)}`;

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      await generateShareCode(report.ID);
    } catch (e) {
      console.error('generateShareCode failed:', e);
      setError(e.message || 'Kon de beveiligingscode niet aanmaken.');
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    setError(null);
    try {
      await clearShareCode(report);
    } catch (e) {
      console.error('clearShareCode failed:', e);
      setError(e.message || 'Kon de beveiligingscode niet verwijderen.');
    } finally {
      setBusy(false);
    }
  };

  /** @param {'open'|'secure'} nextMode */
  const switchModeHandler = async (nextMode) => {
    if (nextMode === mode || busy) return;
    if (nextMode === 'open') {
      if (codeRequired) return;
      setPendingConfirm('switch-to-open');
    } else {
      await generate();
    }
  };

  const regenerateHandler = () => {
    if (busy) return;
    setPendingConfirm('regenerate');
  };

  const confirmHandler = async () => {
    const action = pendingConfirm;
    setPendingConfirm(null);
    if (action === 'switch-to-open') {
      await clear();
    } else if (action === 'regenerate') {
      await generate();
    }
  };

  const cancelConfirmHandler = () => setPendingConfirm(null);

  /** @param {string} value @param {string} label */
  const copyHandler = async (value, label) => {
    await navigator.clipboard.writeText(value);
    setCopied(label);
    setTimeout(() => setCopied(null), 1500);
  };

  const confirmCopy = pendingConfirm === 'switch-to-open'
    ? {
        title: 'Open link gebruiken?',
        body: 'De huidige code stopt direct met werken. Iedereen met de link kan de rapportage dan openen.',
        confirmLabel: 'Ja, open link gebruiken',
      }
    : pendingConfirm === 'regenerate'
    ? {
        title: 'Nieuwe code aanmaken?',
        body: 'De huidige code stopt direct met werken. Een nieuwe code wordt aangemaakt.',
        confirmLabel: 'Ja, nieuwe code',
      }
    : null;

  const content = pendingConfirm && confirmCopy ? (
    <div className="share-report-modal share-report-confirm">
      <h3 className="share-report-confirm-title">{confirmCopy.title}</h3>
      <p className="share-report-confirm-body">{confirmCopy.body}</p>
      <div className="share-report-confirm-actions">
        <button onClick={cancelConfirmHandler} className="button-simple button-simple--ghost">
          Annuleren
        </button>
        <button onClick={confirmHandler} className="button-simple button-simple--danger">
          {confirmCopy.confirmLabel}
        </button>
      </div>
    </div>
  ) : (
    <div className="share-report-modal">

      <fieldset className="share-mode-picker" disabled={busy}>
        <legend className="share-mode-picker-legend">Hoe wil je deze rapportage delen?</legend>

        <label className={`share-mode-option ${mode === 'open' ? 'is-selected' : ''}`}>
          <input
            type="radio"
            name="share-mode"
            value="open"
            checked={mode === 'open'}
            disabled={codeRequired}
            onChange={() => switchModeHandler('open')}
          />
          <span className="share-mode-option-body">
            <span className="share-mode-option-title">Open link</span>
            <span className="share-mode-option-description">
              Iedereen met de link kan de rapportage openen.
            </span>
          </span>
        </label>

        <label className={`share-mode-option ${mode === 'secure' ? 'is-selected' : ''}`}>
          <input
            type="radio"
            name="share-mode"
            value="secure"
            checked={mode === 'secure'}
            onChange={() => switchModeHandler('secure')}
          />
          <span className="share-mode-option-body">
            <span className="share-mode-option-title">Beveiligde link met code</span>
            <span className="share-mode-option-description">
              De ontvanger heeft de link én een 6-cijferige code nodig.
            </span>
          </span>
        </label>
      </fieldset>

      {codeRequired && (
        <p className="share-report-tip">
          Deze rapportage bevat gegevens van één deelnemer. Daarom kan hij alleen met een code
          gedeeld worden. Maak de deelnemer leeg in de kolom 'Deelnemer' om een open link te gebruiken.
        </p>
      )}

      {busy && (
        <div className="share-report-busy">
          <img className="share-report-spinner" src={spinner} alt="Bezig..." />
        </div>
      )}

      {!busy && (
        <div className="share-report-card">
          <label>Link</label>
          <div className="share-report-row">
            <input type="text" readOnly value={url} onFocus={e => e.target.select()} />
            <CopyLink link={getOpenUrl(report.ID)} />
          </div>
        </div>
      )}

      {!busy && mode === 'secure' && code && (
        <div className="share-report-card">
          <label>Code</label>
          <div className="share-report-row">
            <span className="share-report-code">{formatCode(code)}</span>
            <button onClick={() => copyHandler(code, 'code')} className="button-simple">
              {copied === 'code' ? 'Gekopieerd' : 'Kopieer'}
            </button>
          </div>

          <p className="share-report-tip">
            Tip: deel link en code via verschillende kanalen
            (bijvoorbeeld link via e-mail, code via een chatbericht).
          </p>

          <button onClick={regenerateHandler} className="share-report-regenerate">
            Genereer nieuwe code (oude stopt direct)
          </button>
        </div>
      )}

      {!busy && (
        <div className="share-report-card">
          <label>PDF</label>
          <div className="share-report-row">
            <button onClick={() => history.push(getPrintUrl(report.ID))} className="template-action-button secondary">
              <PictureAsPdfOutlinedIcon />
              <span>PDF maken</span>
            </button>
          </div>
        </div>
      )}

      {error && <p className="share-report-error">{error}</p>}
    </div>
  );

  return (
    <Modal
      modalOpen={modalOpen}
      setModalOpen={setModalOpen}
      pageHeader="Delen"
      confirm={() => setModalOpen(false)}
      content={content}
    />
  );
};

export default ShareReportModal;
